import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { SocketContext } from '@app/context/SocketContext';
import FechaFunction from '@app/functions/FechaFunction';

/**
 * Tarjeta de notificacion estilo Material Design 3.
 *
 * Soporta dos tipos:
 *  - type="ticket"     -> item con formato TicketNotify (id_tkntf, accion_tkntf, ...)
 *  - type="comentario" -> item con formato ComentarioNotify (id_comntf, isInterno_comntf, ...)
 *
 * Emite el evento socket correspondiente para marcar como vista y navega al ticket.
 */

const ACCIONES_TICKET = ['APERTURA', 'CIERRE POR UNA SITUACION EXTERNA', 'CIERRE INVALIDO / MAL CARGADO', 'CIERRE', 'RE-APERTURA'];

// Paleta base para el avatar segun tipo/accion.
const AVATAR_STYLES = {
    ticket_apertura: { bg: '#E8F5E9', color: '#2E7D32', icon: 'fas fa-plus-circle' },
    ticket_cierre: { bg: '#FFEBEE', color: '#C62828', icon: 'fas fa-check-circle' },
    ticket_reapertura: { bg: '#FFF3E0', color: '#EF6C00', icon: 'fas fa-redo-alt' },
    ticket_asignacion: { bg: '#E3F2FD', color: '#1565C0', icon: 'fas fa-user-check' },
    ticket_default: { bg: '#EDE7F6', color: '#5E35B1', icon: 'fas fa-ticket-alt' },
    coment_interno: { bg: '#FCE4EC', color: '#AD1457', icon: 'fas fa-lock' },
    coment_publico: { bg: '#E0F7FA', color: '#00838F', icon: 'fas fa-comment-alt' }
};

const getAvatar = (type, item) => {
    if (type === 'ticket') {
        const accion = item?.accion_tkntf;
        if (accion === 'APERTURA') return AVATAR_STYLES.ticket_apertura;
        if (accion === 'RE-APERTURA') return AVATAR_STYLES.ticket_reapertura;
        if (accion && accion.startsWith('CIERRE')) return AVATAR_STYLES.ticket_cierre;
        if (!ACCIONES_TICKET.includes(accion)) return AVATAR_STYLES.ticket_asignacion;
        return AVATAR_STYLES.ticket_default;
    }
    return item?.isInterno_comntf ? AVATAR_STYLES.coment_interno : AVATAR_STYLES.coment_publico;
};

const buildTicketMessage = (item) => {
    const nombre = item?.senduser?.nombre_p || '';
    const accion = item?.accion_tkntf;
    if (ACCIONES_TICKET.includes(accion)) {
        const verbo = (accion === 'APERTURA' || accion === 'RE-APERTURA') ? 'realizo la' : 'realizo el';
        return (
            <>
                {`El personal ${nombre} ${verbo} `}
                <b>{accion}</b>
                {' del ticket '}
                <b>{`Nro ${item?.ticketId_tkntf}`}</b>
                .
            </>
        );
    }
    return (
        <>
            {`${nombre} ${item?.tipoDestino_tkntf === 'DNI' ? 'te asigno en el ticket' : 'asigno al grupo en el ticket'} `}
            <b>{`Nro ${item?.ticketId_tkntf}`}</b>
        </>
    );
};

const buildComentarioMessage = (item) => {
    const nombre = item?.senduser?.nombre_p || '';
    const encabezado = nombre === 'Nubicom S.R.L' ? `${nombre} ` : `El personal ${nombre} `;
    return (
        <>
            {encabezado}
            realizo un comentario
            <b>{item?.isInterno_comntf ? ' Interno ' : ' '}</b>
            en el ticket
            <b>{` Nro ${item?.ticketId_comntf}`}</b>
            .
        </>
    );
};

const NotificationCardM3 = ({ type, item, onNavigate }) => {
    const navigate = useNavigate();
    const { sockets } = useContext(SocketContext);

    const isTicket = type === 'ticket';
    const unread = isTicket ? !item?.visto_tkntf : !item?.visto_comntf;
    const seenAt = isTicket ? item?.visto_tkntf : item?.visto_comntf;
    const ticketId = isTicket ? item?.ticketId_tkntf : item?.ticketId_comntf;

    const avatar = getAvatar(type, item);

    const handleClick = () => {
        if (unread) {
            const eventName = isTicket ? 'newViewTicketNotify' : 'newViewComentarioNotify';
            const notifId = isTicket ? item?.id_tkntf : item?.id_comntf;
            sockets?.emit(eventName, notifId);
        }
        if (onNavigate) onNavigate();
        navigate(`/tickets/${ticketId}/ver`);
    };

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={handleClick}
            onKeyDown={(e) => { if (e.key === 'Enter') handleClick(); }}
            className="m3-notif-card"
            style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                padding: '14px 16px 14px 20px',
                marginBottom: '8px',
                borderRadius: '16px',
                cursor: 'pointer',
                backgroundColor: unread ? '#F5F9FF' : '#FFFFFF',
                border: `1px solid ${unread ? '#D0E2FF' : '#E5E7EB'}`,
                transition: 'background-color .15s ease, box-shadow .15s ease, transform .1s ease',
                outline: 'none'
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = unread ? '#EBF3FF' : '#F5F6F7';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(15, 23, 42, 0.08)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = unread ? '#F5F9FF' : '#FFFFFF';
                e.currentTarget.style.boxShadow = 'none';
            }}
        >
            {/* Indicador lateral (barra) para no leidas */}
            {unread && (
                <span
                    aria-hidden="true"
                    style={{
                        position: 'absolute',
                        left: 6,
                        top: 14,
                        bottom: 14,
                        width: 4,
                        borderRadius: 4,
                        backgroundColor: '#1976D2'
                    }}
                />
            )}

            {/* Avatar / icono */}
            <div
                style={{
                    flexShrink: 0,
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: avatar.bg,
                    color: avatar.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px'
                }}
            >
                <i className={avatar.icon} />
            </div>

            {/* Contenido */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div
                    style={{
                        fontSize: '14px',
                        lineHeight: 1.4,
                        color: '#1F2937',
                        marginBottom: 4,
                        wordBreak: 'break-word'
                    }}
                >
                    {isTicket ? buildTicketMessage(item) : buildComentarioMessage(item)}
                </div>
                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '10px',
                        fontSize: '11.5px',
                        color: '#6B7280'
                    }}
                >
                    {item?.createdAt && (
                        <span>
                            <i className="far fa-clock" style={{ marginRight: 4 }} />
                            {FechaFunction.timeAgoMentions(new Date(item.createdAt))}
                        </span>
                    )}
                    {seenAt && (
                        <span>
                            <i className="fas fa-eye" style={{ marginRight: 4 }} />
                            {FechaFunction.format(new Date(seenAt), 'dd/MM/yyyy HH:mm')}
                        </span>
                    )}
                </div>
            </div>

            {/* Dot no-leido a la derecha */}
            {unread && (
                <span
                    aria-hidden="true"
                    style={{
                        flexShrink: 0,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: '#1976D2',
                        alignSelf: 'center'
                    }}
                />
            )}
        </div>
    );
};

export default NotificationCardM3;
